import React, { useState, useEffect, useRef } from 'react';
import WaveformVisualizer from '../../Common/WaveformVisualizer.jsx';
import SoundAvatar from '../../Common/SoundAvatar.jsx';
import useClipLookup from '../../Common/useClipLookup.js';
import useAudioEngine from '../../../audio/useAudioEngine.js';
import { computeAuthorBonus } from '../../../../../shared/scoring.js';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import '../../../styles/theme.css';

/**
 * DuelReveal Component - Renders Sound Off head-to-head matchup reveals and voting.
 * Matches design/canvas/HostReveal.dc.html.
 *
 * Audio-is-serial: Exactly one submission plays at a time.
 */
export function DuelReveal({
  duel,
  currentRound = 1,
  totalRounds = 3,
  onVote = () => {},
  onDuelComplete = () => {},
  currentUserId = null,
  isHost = false,
}) {
  const lookupClip = useClipLookup();
  const audio = useAudioEngine(isHost ? 'host' : 'player');
  const [playbackStage, setPlaybackStage] = useState('IDLE'); // IDLE, PLAYER_A, PLAYER_B, VOTING, SCORES
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [currentClipPlaying, setCurrentClipPlaying] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);
  const activeHandleRef = useRef(null);

  const promptText = duel?.promptText || duel?.prompt || 'Sound Off Challenge';
  const authorName = duel?.authorName || duel?.promptAuthor || 'Anonymous';
  const duelistA = duel?.duelistA || { id: 'p1', name: 'Player A', avatar: 'seed_a' };
  const duelistB = duel?.duelistB || { id: 'p2', name: 'Player B', avatar: 'seed_b' };
  const subA = duel?.subA || duel?.submissions?.[duelistA.id] || [];
  const subB = duel?.subB || duel?.submissions?.[duelistB.id] || [];

  const isDuelist = currentUserId && (currentUserId === duelistA.id || currentUserId === duelistB.id);
  const isEligibleVoter = !isDuelist;

  // Track progress events from audio engine
  useEffect(() => {
    if (!audio?.engine) return;

    const handleProgress = (data) => {
      setPlaybackProgress(data.progress || 0);
    };

    audio.engine.on('progress', handleProgress);
    return () => {
      if (audio?.engine) {
        audio.engine.off('progress', handleProgress);
      }
    };
  }, [audio?.engine]);

  // Clean up playback on unmount
  useEffect(() => {
    return () => {
      if (activeHandleRef.current) {
        try {
          activeHandleRef.current.stop();
        } catch {}
      }
    };
  }, []);

  const playDuelistAudio = async (which, clips, playerName, playerAvatar, autoAdvance = true) => {
    if (!clips || clips.length === 0) {
      if (autoAdvance) {
        if (which === 'A') {
          setPlaybackStage('PLAYER_B');
        } else {
          setPlaybackStage('VOTING');
        }
      }
      return;
    }

    setPlaybackStage(which === 'A' ? 'PLAYER_A' : 'PLAYER_B');
    setIsPlaying(true);
    setPlaybackProgress(0);

    const firstClip = typeof clips[0] === 'object' ? clips[0] : lookupClip(clips[0]);
    setCurrentClipPlaying({
      player: playerName,
      avatar: playerAvatar,
      clipLabel: formatClipTitle(firstClip || `${playerName}'s Clip`)
    });

    const sequenceItems = clips.map((c, i) => {
      const fullClip = typeof c === 'object' ? c : lookupClip(c);
      return {
        clipId: fullClip?.id || (typeof c === 'string' ? c : `clip_${i}`),
        buffer: fullClip?.buffer,
        gap: i === 0 ? 0 : 0.4
      };
    });

    try {
      const handle = await audio.playSequence(sequenceItems, {
        onProgress: (data) => {
          setPlaybackProgress(data.progress || 0);
          const activeIndex = data.clipIndex || 0;
          const activeItem = clips[activeIndex];
          const activeFullClip = typeof activeItem === 'object' ? activeItem : lookupClip(activeItem);
          setCurrentClipPlaying({
            player: playerName,
            avatar: playerAvatar,
            clipLabel: formatClipTitle(activeFullClip || `${playerName}'s Clip ${activeIndex + 1}`)
          });
        },
        onEnded: () => {
          setIsPlaying(false);
          setPlaybackProgress(1.0);
          activeHandleRef.current = null;

          if (autoAdvance) {
            setTimeout(() => {
              if (which === 'A') {
                setPlaybackStage('PLAYER_B');
              } else {
                setPlaybackStage('VOTING');
              }
            }, 600);
          }
        }
      });

      activeHandleRef.current = handle;
    } catch {
      setIsPlaying(false);
      if (autoAdvance) {
        if (which === 'A') {
          setPlaybackStage('PLAYER_B');
        } else {
          setPlaybackStage('VOTING');
        }
      }
    }
  };

  // Host auto-play effect
  useEffect(() => {
    if (!isHost) return;

    let timer;
    if (playbackStage === 'IDLE') {
      timer = setTimeout(() => {
        playDuelistAudio('A', subA, duelistA.name, duelistA.avatar, true);
      }, 800);
    } else if (playbackStage === 'PLAYER_B' && !isPlaying && !activeHandleRef.current) {
      timer = setTimeout(() => {
        playDuelistAudio('B', subB, duelistB.name, duelistB.avatar, true);
      }, 500);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [playbackStage, isHost, isPlaying]);

  const handleCastVote = (choice) => {
    if (!isEligibleVoter || hasVoted) return;
    setHasVoted(true);
    setSelectedVote(choice);
    if (onVote) onVote(choice);
  };

  // Compute author bonus
  const votesA = duel?.votesA || (duel?.votes ? Object.values(duel.votes).filter(v => v === 'A').length : 0);
  const votesB = duel?.votesB || (duel?.votes ? Object.values(duel.votes).filter(v => v === 'B').length : 0);
  const authorBonus = duel?.authorBonus !== undefined ? duel.authorBonus : computeAuthorBonus(votesA, votesB);

  return (
    <div
      className="duel-reveal-container"
      data-testid="duel-reveal"
      style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '24px 32px',
        background: 'var(--sd-ground, #08090B)',
        color: 'var(--sd-ink, #EDEFF2)',
        borderRadius: '20px',
        border: '1px solid var(--sd-line, #1A1F25)',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box',
      }}
    >
      {/* Prompt Banner */}
      <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{
          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
          fontSize: '11.5px',
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'var(--sd-dim, #6E7982)',
          marginBottom: '10px',
        }}>
          {authorName} asked for
        </div>
        <h2
          data-testid="duel-prompt"
          style={{
            fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
            fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
            fontWeight: 800,
            lineHeight: 1.2,
            letterSpacing: '-0.03em',
            color: 'var(--sd-ink, #EDEFF2)',
            margin: '0 auto 8px auto',
            maxWidth: '30ch',
          }}
        >
          "{promptText}"
        </h2>
      </div>

      {/* Duelists Head-to-Head */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 60px 1fr',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        {/* Duelist A Card */}
        <div
          data-testid="duelist-a-card"
          style={{
            background: playbackStage === 'PLAYER_A' ? 'rgba(58, 207, 207, 0.08)' : 'var(--sd-panel, #0D1015)',
            border: `1px solid ${playbackStage === 'PLAYER_A' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-line-2, #1C2229)'}`,
            borderRadius: '16px',
            padding: '20px',
            textAlign: 'center',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>
            <SoundAvatar clip={lookupClip(duelistA.avatar)} seed={duelistA.name} size="md" />
          </div>
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--sd-ink, #EDEFF2)' }}>{duelistA.name}</div>
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: playbackStage === 'PLAYER_A' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-dim, #6E7982)',
            marginTop: '6px',
          }}>
            {playbackStage === 'PLAYER_A' ? 'On air' : 'Player A'}
          </div>
          <div style={{ marginTop: '12px' }}>
            <button
              type="button"
              data-testid="play-duelist-a-btn"
              onClick={() => playDuelistAudio('A', subA, duelistA.name, duelistA.avatar, false)}
              style={{
                padding: '8px 16px',
                minHeight: '44px',
                minWidth: '44px',
                borderRadius: '10px',
                background: 'var(--sd-panel-2, #0E1116)',
                color: 'var(--sd-ink-2, #C6CDD4)',
                border: '1px solid var(--sd-line, #1A1F25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              ▶ Play Player A
            </button>
          </div>
        </div>

        {/* VS Label */}
        <div style={{
          textAlign: 'center',
          fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
          fontWeight: 700,
          fontSize: '1.2rem',
          color: 'var(--sd-dim, #6E7982)',
        }}>
          VS
        </div>

        {/* Duelist B Card */}
        <div
          data-testid="duelist-b-card"
          style={{
            background: playbackStage === 'PLAYER_B' ? 'rgba(224, 164, 88, 0.08)' : 'var(--sd-panel, #0D1015)',
            border: `1px solid ${playbackStage === 'PLAYER_B' ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line-2, #1C2229)'}`,
            borderRadius: '16px',
            padding: '20px',
            textAlign: 'center',
            transition: 'all 0.2s ease',
          }}
        >
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'center' }}>
            <SoundAvatar clip={lookupClip(duelistB.avatar)} seed={duelistB.name} size="md" />
          </div>
          <div style={{ fontWeight: 800, fontSize: '1.2rem', color: 'var(--sd-ink, #EDEFF2)' }}>{duelistB.name}</div>
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: playbackStage === 'PLAYER_B' ? 'var(--sd-amber, #E0A458)' : 'var(--sd-dim, #6E7982)',
            marginTop: '6px',
          }}>
            {playbackStage === 'PLAYER_B' ? 'On air' : 'Player B'}
          </div>
          <div style={{ marginTop: '12px' }}>
            <button
              type="button"
              data-testid="play-duelist-b-btn"
              onClick={() => playDuelistAudio('B', subB, duelistB.name, duelistB.avatar, false)}
              style={{
                padding: '8px 16px',
                minHeight: '44px',
                minWidth: '44px',
                borderRadius: '10px',
                background: 'var(--sd-panel-2, #0E1116)',
                color: 'var(--sd-ink-2, #C6CDD4)',
                border: '1px solid var(--sd-line, #1A1F25)',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              ▶ Play Player B
            </button>
          </div>
        </div>
      </div>

      {/* Visual Waveform Header — Always visible on duel screen */}
      <div data-testid="duel-playback-sequence">
        <WaveformVisualizer
          isPlaying={isPlaying}
          progress={playbackProgress}
          duration={4.0}
          playerName={currentClipPlaying?.player || duelistA.name}
          playerAvatar={currentClipPlaying?.avatar || duelistA.avatar || '🎧'}
          clipTitle={currentClipPlaying?.clipLabel || `${duelistA.name}'s Sound Response`}
          color={playbackStage === 'PLAYER_B' ? 'var(--sd-amber, #E0A458)' : 'var(--sd-teal, #3ACFCF)'}
          onAir={isPlaying}
        />
      </div>

      {/* Voting Phase Section */}
      {(playbackStage === 'VOTING' || playbackStage === 'SCORES') && (
        <div
          data-testid="voting-section"
          style={{
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
            padding: '24px',
            textAlign: 'center',
          }}
        >
          {isDuelist ? (
            <div data-testid="duelist-excluded-message" style={{ color: 'var(--sd-rose, #E4738A)', fontWeight: 700, padding: '12px' }}>
              🚫 Duelists are excluded from voting on their own matchup!
            </div>
          ) : (
            <div>
              <h3 style={{
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                fontSize: '1.2rem',
                color: 'var(--sd-ink, #EDEFF2)',
                marginBottom: '16px',
                fontWeight: 700,
              }}>
                {hasVoted ? '✓ Vote cast!' : 'Who had the better sounds? Vote now!'}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <button
                  type="button"
                  data-testid="vote-btn-a"
                  onClick={() => handleCastVote('A')}
                  disabled={hasVoted}
                  style={{
                    minHeight: '52px',
                    minWidth: '44px',
                    borderRadius: '12px',
                    background: selectedVote === 'A' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-panel-2, #0E1116)',
                    color: selectedVote === 'A' ? 'var(--sd-ground, #08090B)' : 'var(--sd-ink, #EDEFF2)',
                    border: `1px solid ${selectedVote === 'A' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-line, #1A1F25)'}`,
                    fontWeight: 700,
                    fontSize: '15px',
                    cursor: hasVoted ? 'default' : 'pointer',
                    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                    transition: 'all 0.15s ease',
                  }}
                >
                  Vote {duelistA.name} (A)
                </button>

                <button
                  type="button"
                  data-testid="vote-btn-b"
                  onClick={() => handleCastVote('B')}
                  disabled={hasVoted}
                  style={{
                    minHeight: '52px',
                    minWidth: '44px',
                    borderRadius: '12px',
                    background: selectedVote === 'B' ? 'var(--sd-amber, #E0A458)' : 'var(--sd-panel-2, #0E1116)',
                    color: selectedVote === 'B' ? 'var(--sd-ground, #08090B)' : 'var(--sd-ink, #EDEFF2)',
                    border: `1px solid ${selectedVote === 'B' ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line, #1A1F25)'}`,
                    fontWeight: 700,
                    fontSize: '15px',
                    cursor: hasVoted ? 'default' : 'pointer',
                    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                    transition: 'all 0.15s ease',
                  }}
                >
                  Vote {duelistB.name} (B)
                </button>
              </div>
            </div>
          )}

          {/* Score & Author Bonus Display */}
          <div data-testid="author-bonus-display" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--sd-line, #1A1F25)' }}>
            <div style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '0.9rem',
              color: 'var(--sd-teal, #3ACFCF)',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}>
              💡 Prompt Author Bonus: +{authorBonus} pts
            </div>
            <div style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '0.8rem',
              color: 'var(--sd-muted, #79838D)',
              marginTop: '4px',
            }}>
              Scaled to how contested the vote was (Votes A: {votesA}, Votes B: {votesB})
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DuelReveal;
